import {
  CraAttributeName,
  CodingAttributes,
  FacilityType,
  GradeType,
  HeavyVehicleFlowType,
  IntersectionApproachType,
  NumberOfLanesType,
  PresenceType,
} from "@keita/lanepatrol-sdk";
import { RootStore } from "../../shared/stores/RootStore";
import { CraNodeCoding } from "../models/CraNodeCoding";

export namespace SuggestionConditions {
  export const noAdjacentSidewalk = (attr: CodingAttributes) =>
    attr.facilityType === FacilityType.Sidewalk;

  export const noDelineation = (attr: CodingAttributes) =>
    attr.facilityType === FacilityType.MixedTrafficRoadLane;

  export const noIntersectionOrRoadCrossing = (attr: CodingAttributes) =>
    attr.intersectionOrRoadCrossing === PresenceType.NotPresent;

  export const noAdjacentRoadLane = (attr: CodingAttributes) =>
    (attr.facilityType === FacilityType.OffRoadBicyclePath ||
      attr.facilityType === FacilityType.MultiUsePath ||
      attr.facilityType === FacilityType.Sidewalk) &&
    attr.adjacentFeatures0to1m?.roadLane === PresenceType.NotPresent &&
    attr.adjacentFeatures1to3m?.roadLane === PresenceType.NotPresent;
}

export class SuggestionEngine {
  rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
  }

  makeSuggestions(nodeCoding: CraNodeCoding) {
    const { codingStore } = this.rootStore;
    const { attributes } = nodeCoding;

    if (SuggestionConditions.noAdjacentSidewalk(attributes)) {
      codingStore.suggestValue(
        nodeCoding,
        CraAttributeName.AdjacentFeatures0to1mSidewalk,
        PresenceType.NotPresent,
        true,
      );
      codingStore.suggestValue(
        nodeCoding,
        CraAttributeName.AdjacentFeatures1to3mSidewalk,
        PresenceType.NotPresent,
        true,
      );
    }

    if (SuggestionConditions.noDelineation(attributes)) {
      codingStore.suggestValue(
        nodeCoding,
        CraAttributeName.Delineation,
        PresenceType.NotPresent,
        true,
      );
    }

    if (SuggestionConditions.noIntersectionOrRoadCrossing(attributes)) {
      codingStore.suggestValue(
        nodeCoding,
        CraAttributeName.IntersectionApproach,
        IntersectionApproachType.SeparateOrNA,
        true,
      );
      codingStore.suggestValue(
        nodeCoding,
        CraAttributeName.NumberOfLanesIntersectingRoad,
        NumberOfLanesType.OnePerDirection,
        false,
      );
      codingStore.suggestValue(
        nodeCoding,
        CraAttributeName.CrossingFacility,
        PresenceType.NotPresent,
        true,
      );
    }

    if (SuggestionConditions.noAdjacentRoadLane(attributes)) {
      codingStore.suggestValue(
        nodeCoding,
        CraAttributeName.NumberOfLanesAdjacentRoad,
        NumberOfLanesType.OnePerDirection,
        false,
      );
      codingStore.suggestValue(nodeCoding, CraAttributeName.RoadAADT, 0, true);
      codingStore.suggestValue(
        nodeCoding,
        CraAttributeName.RoadOperatingSpeed,
        0,
        true,
      );
      codingStore.suggestValue(
        nodeCoding,
        CraAttributeName.RoadSpeedLimit,
        0,
        true,
      );
      codingStore.suggestValue(
        nodeCoding,
        CraAttributeName.HeavyVehicleFlow,
        HeavyVehicleFlowType.LowOrRestrictedAccess,
        true,
      );
    }
  }

  suggestGradeType(nodeId: string, slope: number) {
    const { codingStore } = this.rootStore;
    const nodeCoding = codingStore.getFromSelectedWay()!.get(nodeId);
    if (nodeCoding) {
      codingStore.suggestValue(
        nodeCoding,
        CraAttributeName.Grade,
        slope < 5 ? GradeType.LessThan5degrees : GradeType.MoreThan5degrees,
        false,
      );
    }
  }
}
